import { useState } from 'react';
import { toast } from 'react-toastify';
import { 
  FaPlus, 
  FaEdit, 
  FaSave, 
  FaTimes,
  FaCog
} from 'react-icons/fa';
import { 
  createProjectPhase, 
  updateProjectPhase
} from '../../api/projects';

interface PhaseManagerProps {
  token: string | undefined;
  projectId: string;
  phases: ProjectPhaseInterface[];
  onClose?: () => void;
  onPhasesUpdate?: () => void;
  isModal?: boolean;
}

export const PhaseManager = ({ 
  token, 
  projectId, 
  phases, 
  onClose, 
  onPhasesUpdate, 
  isModal = true 
}: PhaseManagerProps) => {
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newPhaseName, setNewPhaseName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Create new phase
  const handleCreate = async () => {
    if (!token || !newPhaseName.trim()) return;

    try {
      setLoading(true);
      const newPhase = {
        id: crypto.randomUUID(),
        project_id: projectId,
        name: newPhaseName.trim(),
        status: 'NOT_STARTED' as const
      };

      await createProjectPhase(token, projectId, newPhase);
      setNewPhaseName('');
      setIsCreating(false);
      toast.success('Phase created successfully');
      
      // Trigger parent update
      if (onPhasesUpdate) {
        await onPhasesUpdate();
      }
    } catch (error) {
      toast.error('Failed to create phase');
      console.error('Error creating phase:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update phase
  const handleUpdate = async (phaseId: string, name: string) => {
    if (!token || !name.trim()) return;

    try {
      setLoading(true);
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) return;

      const updatedPhase = {
        ...phase,
        name: name.trim()
      };

      await updateProjectPhase(token, projectId, phaseId, updatedPhase);
      setEditingId(null);
      setEditingName('');
      toast.success('Phase updated successfully');
      
      // Trigger parent update
      if (onPhasesUpdate) {
        await onPhasesUpdate();
      }
    } catch (error) {
      toast.error('Failed to update phase');
      console.error('Error updating phase:', error);
    } finally {
      setLoading(false);
    }
  };



  const startEditing = (phase: ProjectPhaseInterface) => {
    setEditingId(phase.id);
    setEditingName(phase.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const content = (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <FaCog className="w-5 h-5 text-blue-500" />
          <h2 className="text-xl font-semibold font-['Arcadia_Display',system-ui,sans-serif]">
            Manage Phases
          </h2>
        </div>
        {isModal && onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <FaTimes className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Create New Phase */}
        <div className="mb-6">
          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <FaPlus className="w-4 h-4" />
              <span>Add New Phase</span>
            </button>
          ) : (
            <div className="flex items-center space-x-3 p-4 border border-blue-200 rounded-md bg-blue-50">
              <input
                type="text"
                value={newPhaseName}
                onChange={(e) => setNewPhaseName(e.target.value)}
                placeholder="Enter phase name..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreate();
                  } else if (e.key === 'Escape') {
                    setIsCreating(false);
                    setNewPhaseName('');
                  }
                }}
              />
              <button
                onClick={handleCreate}
                disabled={!newPhaseName.trim() || loading}
                className="px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <FaSave className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewPhaseName('');
                }}
                className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Phases List */}
        {loading && !isCreating ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading phases...</span>
          </div>
        ) : phases.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FaCog className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No phases found</p>
            <p className="text-sm">Create your first phase to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {phases.map((phase) => (
              <div
                key={phase.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              >
                {editingId === phase.id ? (
                  <div className="flex items-center space-x-3 flex-1">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdate(phase.id, editingName);
                        } else if (e.key === 'Escape') {
                          cancelEditing();
                        }
                      }}
                    />
                    <button
                      onClick={() => handleUpdate(phase.id, editingName)}
                      disabled={!editingName.trim() || loading}
                      className="px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <FaSave className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                    >
                      <FaTimes className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-gray-900">{phase.name}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        phase.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        phase.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {phase.status?.replace('_', ' ') || 'Not Started'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => startEditing(phase)}
                        className="p-2 text-blue-500 hover:bg-blue-100 rounded-md transition-colors"
                        title="Edit"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
          {content}
        </div>
      </div>
    );
  }

  return content;
}; 